import { useMemo } from 'react'
import Decimal from 'decimal.js'
import styled from 'styled-components'
import { DryRunReduceResult, PositionConfigInfo, TypeArgument, USDC } from '@kunalabs-io/kai'

import { IconInfo } from 'components/Icons/InfoIcon'
import { ReversedWrapper } from 'components/ReversedWrapper/ReversedWrapper'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { Typography } from 'components/Typography/Typography'
import { useTokenPrice } from 'utils/useTokenPrice'
import { formatPercentage, getPercentageChange, SMALL_PCT_ROUNDED } from '../utils'
import { useIsMobile } from 'utils/useIsMobile'
import { ReductionInfoSubmit } from '../../types'
import { getFormattedUsdValue } from 'utils/usdFormatters'

const PositionBreakdownTitle = styled(Typography)`
  margin: 16px 0;
  display: flex;
  align-items: center;
  & > div {
    display: flex;
    cursor: pointer;
    & > svg {
      margin-left: 6px;
      width: 16px;
      height: 16px;
      & > path {
        stroke: ${p => p.theme.colors.text.secondary};
      }
    }
  }
`

const PositionBreakdownContainer = styled.div`
  padding: 20px 16px;
  border-radius: 8px;
  border: 1px solid ${p => p.theme.colors.border};
  background: ${p => p.theme.colors.background.secondary};
`

const PositionBreakdownContainerItem = styled.div`
  display: flex;
  justify-content: space-between;
  position: relative;
`

const PositionBreakdownSymbol = styled(Typography)`
  display: flex;
  img {
    margin-right: 8px;
    border-radius: 50%;
  }
`

const UsdEstimate = styled(Typography)`
  position: absolute;
  right: 0;
`

const Divider = styled.div`
  height: 1px;
  width: 100%;
  background-color: ${p => p.theme.colors.divider};
  margin-top: 22px;
  margin-bottom: 8px;
`

const Total = styled.div`
  display: flex;
  justify-content: space-between;
`

interface Props {
  isReversedPair: boolean
  configInfo: PositionConfigInfo<string, string, TypeArgument>
  reductionInfoSubmit: ReductionInfoSubmit
  dryRunResult?: DryRunReduceResult
  isCalculatingDryRun: boolean
  dryRunError?: Error
}

export const Equity = ({
  isReversedPair,
  configInfo,
  reductionInfoSubmit,
  isCalculatingDryRun,
  dryRunResult,
  dryRunError,
}: Props) => {
  const isMobile = useIsMobile()

  const { isLoading: isLoadingUsdValueX, price: priceX } = useTokenPrice(configInfo.X, USDC)
  const { isLoading: isLoadingUsdValueY, price: priceY } = useTokenPrice(configInfo.Y, USDC)

  const { equityXUsd, equityXUsdDryRun, equityYUsd, equityYUsdDryRun, totalUsd, totalUsdDryRun, percentageChange } =
    useMemo(() => {
      let equityXUsd = ''
      let equityYUsd = ''
      let equityXUsdDryRun = ''
      let equityYUsdDryRun = ''
      let equityXDecimal: Decimal | undefined = undefined
      let equityYDecimal: Decimal | undefined = undefined
      let equityXDecimalDryRun: Decimal | undefined = undefined
      let equityYDecimalDryRun: Decimal | undefined = undefined

      if (priceX && !isLoadingUsdValueX) {
        if (!isCalculatingDryRun && dryRunResult) {
          equityXDecimalDryRun = new Decimal(0).plus(dryRunResult.finalX.toDecimal().mul(priceX.human))
          equityXUsdDryRun = getFormattedUsdValue(equityXDecimalDryRun.toNumber())
        }
        equityXDecimal = new Decimal(0).plus(reductionInfoSubmit.finalX.mul(priceX.human))
        equityXUsd = getFormattedUsdValue(equityXDecimal.toNumber())
      }

      if (priceY && !isLoadingUsdValueY) {
        if (!isCalculatingDryRun && dryRunResult) {
          equityYDecimalDryRun = new Decimal(0).plus(dryRunResult.finalY.toDecimal().mul(priceY.human))
          equityYUsdDryRun = getFormattedUsdValue(equityYDecimalDryRun.toNumber())
        }
        equityYDecimal = new Decimal(0).plus(reductionInfoSubmit.finalY.mul(priceY.human))
        equityYUsd = getFormattedUsdValue(equityYDecimal.toNumber())
      }

      return {
        equityXUsd,
        equityYUsd,
        equityXUsdDryRun,
        equityYUsdDryRun,
        percentageChange:
          equityXDecimal && equityYDecimal && equityXDecimalDryRun && equityYDecimalDryRun
            ? getPercentageChange(equityXDecimal.plus(equityYDecimal), equityXDecimalDryRun.plus(equityYDecimalDryRun))
            : undefined,
        totalUsdDryRun:
          equityXDecimalDryRun && equityYDecimalDryRun
            ? getFormattedUsdValue(equityXDecimalDryRun.plus(equityYDecimalDryRun).toNumber())
            : '',
        totalUsd:
          equityXDecimal && equityYDecimal
            ? getFormattedUsdValue(equityXDecimal.plus(equityYDecimal).toNumber(), { compact: false })
            : '',
      }
    }, [
      priceX,
      isLoadingUsdValueX,
      priceY,
      isLoadingUsdValueY,
      isCalculatingDryRun,
      dryRunResult,
      reductionInfoSubmit.finalX,
      reductionInfoSubmit.finalY,
    ])

  const showDryRunResult = !isCalculatingDryRun && dryRunResult

  const formattedPctChange = percentageChange ? formatPercentage(percentageChange) : ''
  return (
    <div>
      <PositionBreakdownTitle variant="body" color="secondary">
        Equity
        <Tooltip
          content="The value of your own capital in a position, calculated as the difference between your assets and debt."
          position={isMobile ? 'bottom' : 'top'}
          tooltipBoxStyles={isMobile ? { marginLeft: 100, minWidth: 250 } : {}}
        >
          <IconInfo width={16} height={16} />
        </Tooltip>
      </PositionBreakdownTitle>
      <PositionBreakdownContainer>
        <ReversedWrapper
          childX={
            <PositionBreakdownContainerItem style={isReversedPair ? {} : { marginBottom: 20 }}>
              <PositionBreakdownSymbol variant="description" fontWeight="semiBold">
                <img src={configInfo.X.iconUrl} alt={configInfo?.X.displaySymbol} width={24} height={24} />
                {configInfo.X.displaySymbol}
              </PositionBreakdownSymbol>
              <Typography variant="description" fontWeight="semiBold">
                {showDryRunResult ? dryRunResult.finalX.toDecimal().toString() : reductionInfoSubmit.finalX.toString()}
                <UsdEstimate variant="caption" color="secondary">
                  {showDryRunResult ? equityXUsdDryRun : equityXUsd}
                </UsdEstimate>
              </Typography>
            </PositionBreakdownContainerItem>
          }
          childY={
            <PositionBreakdownContainerItem style={isReversedPair ? { marginBottom: 20 } : {}}>
              <PositionBreakdownSymbol variant="description" fontWeight="semiBold">
                <img src={configInfo.Y.iconUrl} alt={configInfo?.Y.displaySymbol} width={24} height={24} />
                {configInfo.Y.displaySymbol}
              </PositionBreakdownSymbol>
              <Typography variant="description" fontWeight="semiBold">
                {showDryRunResult ? dryRunResult.finalY.toDecimal().toString() : reductionInfoSubmit.finalY.toString()}
                <UsdEstimate variant="caption" color="secondary">
                  {showDryRunResult ? equityYUsdDryRun : equityYUsd}
                </UsdEstimate>
              </Typography>
            </PositionBreakdownContainerItem>
          }
          isReversed={isReversedPair}
        />
        <Divider />
        <Total>
          <Typography variant="description" fontWeight="semiBold">
            TOTAL
          </Typography>
          <Typography variant="description" fontWeight="semiBold" style={{ display: 'flex', gap: 8 }}>
            {showDryRunResult ? totalUsdDryRun : totalUsd}
          </Typography>
        </Total>
        {showDryRunResult && percentageChange && formattedPctChange && !dryRunError ? (
          <Typography
            variant="caption"
            color={
              formattedPctChange === SMALL_PCT_ROUNDED
                ? 'success'
                : percentageChange.isNegative()
                  ? 'danger'
                  : 'success'
            }
            style={{ textAlign: 'right' }}
          >
            {formattedPctChange}
          </Typography>
        ) : (
          <div style={{ height: 18 }} />
        )}
      </PositionBreakdownContainer>
    </div>
  )
}
